<script setup lang="ts">
import * as _ from 'lodash-es';
import { onBeforeMount, onBeforeUnmount } from 'vue';
import type { VNode } from 'vue';
import type { ToastMessageOptions, ToastPassThroughMethodOptions } from 'primevue/toast';
import { useToast } from 'primevue/usetoast';

// types
type ToastSeverity = 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast';

interface ToastAnimationState {
  animationId: number;
  remainingTime: number;
  startTime: number;
  originalLife: number;
  messageId: string | number;
}

interface ToastMessageState extends ToastMessageOptions {
  id?: string | number;
  originalLife?: number;
}

// composables
const toast = useToast();

// data
const pausedMessages = new Map<HTMLElement, ToastAnimationState>();

// constants
const SEVERITY_COLORS: Record<ToastSeverity, string> = {
  success: '#224a23',
  info: '#044868',
  warn: '#6d5100',
  error: '#73000c',
  secondary: '#424242',
  contrast: '#ffffff'
};
const DEFAULT_LIFE: number = 3000;
const DEFAULT_CLOSE_LIFE: number = 1000;

// passthrough
const getMessagePT = (options: ToastPassThroughMethodOptions) => {
  const message = _.get(options.props, 'message') as ToastMessageState | undefined;
  const life: number = _.get(message, 'originalLife', DEFAULT_LIFE) as number;
  const messageId: string | number = _.get(message, 'id', '') as string | number;
  const severity: ToastSeverity = (_.get(message, 'severity', 'info') as ToastSeverity) || 'info';
  const progressColor: string = SEVERITY_COLORS[severity] || SEVERITY_COLORS.info;

  return {
    'data-life': life,
    'data-original-life': life,
    'data-message-id': messageId,
    style: { position: 'relative', overflow: 'hidden', paddingBottom: '0.5rem' },
    onVnodeMounted: (vnode: VNode) => {
      const el = vnode.el as HTMLElement;
      if (!el || !life) return;

      createProgressBar(el, messageId, progressColor);
      startProgress(el, messageId, life);
    },
    onMouseenter: pauseProgress,
    onMouseleave: resumeProgress
  };
};

// methods
const createProgressBar = (el: HTMLElement, messageId: string | number, color: string) => {
  const container: HTMLDivElement = document.createElement('div');
  container.style.cssText = 'position: absolute; bottom: 0; left: 0; right: 0; height: 2px';

  const bar: HTMLDivElement = document.createElement('div');
  bar.style.cssText = `height: 100%; background: ${color}; width: 100%; transition: width 0.1s linear;`;
  bar.dataset.messageId = String(messageId);

  container.appendChild(bar);
  el.appendChild(container);
};

const pauseProgress = (event: Event) => {
  const target = event.currentTarget as HTMLElement;
  if (!target) return;

  const data: ToastAnimationState | undefined = pausedMessages.get(target);
  const animationId: number | undefined = data?.animationId;

  if (data && animationId) {
    cancelAnimationFrame(animationId);
    pausedMessages.set(target, { ...data, animationId: 0 });
  }
};

const resumeProgress = (event: Event) => {
  const target = event.currentTarget as HTMLElement;
  if (!target) return;

  const data: ToastAnimationState | undefined = pausedMessages.get(target);
  if (data && data.remainingTime > 0) {
    // Reset the progress bar to full width (100%)
    const progressBar: HTMLElement | null = target.querySelector<HTMLElement>(`[data-message-id="${data.messageId}"]`);
    if (progressBar) {
      // Disable transition temporarily
      progressBar.style.transition = 'none';
      progressBar.style.width = '100%';

      // Force reflow to apply the width change immediately
      progressBar.getBoundingClientRect();

      // Re-enable transition
      progressBar.style.transition = 'width 0.1s linear';
    }

    // Update the original-life attribute to 1 second for the new animation
    target.setAttribute('data-original-life', `${DEFAULT_CLOSE_LIFE}`);

    startProgress(target, data.messageId, DEFAULT_CLOSE_LIFE);
  }
};

const startProgress = (messageEl: HTMLElement, messageId: string | number, totalLife: number) => {
  if (!totalLife) return;

  const startTime: number = _.now();
  const originalLife: number = parseInt(messageEl.getAttribute('data-original-life') || String(totalLife), 10);

  const updateProgress = () => {
    const elapsed: number = _.now() - startTime;
    const remainingTime: number = Math.max(0, totalLife - elapsed);
    const progress: number = Math.max(0, (remainingTime / originalLife) * 100);

    const progressBar: HTMLElement | null = messageEl.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }

    if (remainingTime > 0) {
      const animationId: number = requestAnimationFrame(updateProgress);
      pausedMessages.set(messageEl, {
        animationId,
        remainingTime,
        startTime,
        originalLife,
        messageId
      });
    } else {
      // Fade out the toast instead of collapsing
      messageEl.style.transition = 'opacity 0.5s ease-out';
      messageEl.style.opacity = '0';
      pausedMessages.delete(messageEl);

      setTimeout(() => {
        messageEl.querySelector<HTMLElement>('[data-pc-section="closebutton"]')?.click();
      }, 500);
    }
  };

  updateProgress();
};

// hooks
onBeforeMount(() => {
  const originalAdd = toast.add.bind(toast) as (message: ToastMessageOptions) => void;
  toast.add = (message: ToastMessageState) => {
    const originalLife: number = message.life || DEFAULT_LIFE;

    if (!message.id) {
      message.id = Date.now() + Math.random();
    }

    originalAdd({ ...message, life: 0, originalLife } as ToastMessageOptions);
  };
});

onBeforeUnmount(() => {
  _.forEach(Array.from(pausedMessages.values()), (data: ToastAnimationState) => {
    if (data.animationId) cancelAnimationFrame(data.animationId);
  });
  pausedMessages.clear();
});
</script>

<template>
  <Toast :pt="{ message: getMessagePT }" />
</template>
